import { useQuery, useReactiveVar } from '@apollo/client';
import { Skeleton } from '@mui/material';
import type React from 'react';
import { useEffect, useRef, useState } from 'react';
import AUDIO_IMAGE from 'src/assets/audio-image.png';
import VideoJS from 'src/components/VideoJs';
import { AUDIO, MEDIA_PATH, SHARED_MEDIA_PATH } from 'src/constants';
import { buildAllFieldsQ } from 'src/dynamoQueries/queryHelpers';
import { getThumbnailFormatVarsByItemType } from 'src/helpers';
import { cleanPayload } from 'src/helpers/common';
import { isVisualMode } from 'src/helpers/mode';
import viewStateVar from 'src/store/viewState';
import type Player from 'video.js/dist/types/player';

interface VideoPreviewProps {
  itemID: string;
  mimetype: string;
  proxyID: string;
  itemType: string;
  isShared?: boolean;
  nextProxyId?: string | null;
  prevProxyId?: string | null;
}

interface FormatData {
  id?: string;
  online?: boolean;
}

const VideoPreview: React.FC<VideoPreviewProps> = ({
  itemID,
  mimetype,
  proxyID,
  itemType,
  isShared = false,
  nextProxyId,
  prevProxyId,
}) => {
  const playerRef = useRef<Player | null>(null);
  const [isLoaded, setIsLoaded] = useState<boolean>(false);

  const viewState = useReactiveVar(viewStateVar);
  const setPlayer = (_player: Player | null) => {
    playerRef.current = _player;
    if (_player) {
      _player.on('loadeddata', () => {
        setIsLoaded(true);
      });
    }
  };
  // Preload next and previous videos, this is for fixing the flashing issue
  useEffect(() => {
    const preloadVideo = (proxyId: string | null | undefined) => {
      if (!proxyId) return;
      const video = document.createElement('video');
      video.preload = 'metadata';
      video.src = `${isShared ? SHARED_MEDIA_PATH : MEDIA_PATH}/${proxyId}`;
    };

    if (nextProxyId) preloadVideo(nextProxyId);
    if (prevProxyId) preloadVideo(prevProxyId);
  }, [nextProxyId, prevProxyId, isShared]);

  const { data: thumbnailData, loading: loadingThumbnailData } = useQuery(buildAllFieldsQ(), {
    skip: !itemID,
    // variables: { asset_Id: itemID, fmtType_Slug: THUMBNAIL_FORMAT_SLUG },
    variables: getThumbnailFormatVarsByItemType(itemID, itemType),
  });

  if (loadingThumbnailData) {
    return (
      <Skeleton
        variant="rectangular"
        animation="wave"
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: '60%',
          height: '60%',
          borderRadius: 2,
          bgcolor: (theme) => theme.palette.action.disabledBackground,
        }}
      />
    );
  }

  const thumbnailFormat: FormatData = cleanPayload(thumbnailData)[0] || {};

  const thumbnailFormatPath = `${MEDIA_PATH}/` + `${thumbnailFormat.id}`;
  const audioPoster = thumbnailFormatPath ? thumbnailFormatPath : AUDIO_IMAGE;
  const mediaPoster = mimetype?.includes(AUDIO) ? audioPoster : thumbnailFormatPath;

  return (
    <div
      style={{
        width: '70%',
        height: '100%',
        margin: 'auto',
        display: 'flex',
        alignItems: 'center',
        opacity: isLoaded ? 1 : 0,
        transition: 'opacity 0.3s ease-in-out',
      }}
    >
      <VideoJS
        enableKeyBindings={isVisualMode(viewState.mode)}
        asset={{ id: itemID }}
        setPlayer={setPlayer}
        videoUrl={`${isShared ? SHARED_MEDIA_PATH : MEDIA_PATH}/` + `${proxyID}`}
        poster={mediaPoster}
        audioPosterMode={!!mimetype?.includes(AUDIO)}
      />
    </div>
  );
};

export default VideoPreview;
